/**
 * Central date/time formatting utilities.
 *
 * Key behaviours:
 *  1. Timestamps from Caspio arrive without a timezone suffix (e.g. "2026-06-16T08:18:46").
 *     JavaScript treats those as *local* time, which breaks for multi-timezone users.
 *     `toDate()` appends "Z" when no offset is present, forcing UTC interpretation so
 *     every value is anchored to a real UTC instant before display.
 *  2. Display timezone: data is stored in UTC and rendered in the active site's IANA
 *     timezone (set via `setDisplayTimeZone`), falling back to the browser's own zone.
 *     Callers may still pass an explicit `timeZone` override per call.
 *  3. Locale is `undefined` — the browser's own locale. `month: 'short'` avoids the
 *     6/16 vs 16/6 ambiguity; datetimes carry a short zone label for unambiguous records.
 */

function toDate(d: Date | string | number): Date {
  if (typeof d === 'string' && d.includes('T') && !/Z$|[+-]\d{2}:\d{2}$/.test(d)) {
    return new Date(`${d}Z`);
  }
  return new Date(d);
}

const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

let displayTimeZone = LOCAL_TZ;

export function setDisplayTimeZone(tz: string | null | undefined): void {
  displayTimeZone = tz && tz.trim() ? tz : LOCAL_TZ;
}

export function getDisplayTimeZone(): string {
  return displayTimeZone;
}

function dateOpts(tz: string): Intl.DateTimeFormatOptions {
  return { day: 'numeric', month: 'short', year: 'numeric', timeZone: tz };
}
function dateTimeOpts(tz: string): Intl.DateTimeFormatOptions {
  return {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZone: tz, timeZoneName: 'short',
  };
}
function timeOpts(tz: string): Intl.DateTimeFormatOptions {
  return { hour: '2-digit', minute: '2-digit', timeZone: tz };
}

/** "Jun 16, 2026" — browser locale, active site timezone */
export function fmtDate(d: Date | string | number, timeZone: string = displayTimeZone): string {
  return toDate(d).toLocaleDateString(undefined, dateOpts(timeZone));
}

/** "Jun 16, 2026, 01:41 PM GMT+11" — browser locale, active site timezone + zone label */
export function fmtDateTime(d: Date | string | number, timeZone: string = displayTimeZone): string {
  return toDate(d).toLocaleString(undefined, dateTimeOpts(timeZone));
}

/** "01:41 PM" — browser locale, active site timezone */
export function fmtTime(d: Date | string | number, timeZone: string = displayTimeZone): string {
  return toDate(d).toLocaleTimeString(undefined, timeOpts(timeZone));
}

// ---------------------------------------------------------------------------
// <input type="datetime-local"> bridging
// ---------------------------------------------------------------------------
//
// A datetime-local input reads and writes a WALL CLOCK with no zone attached,
// and the browser interprets it in the machine's own zone. Everything else in
// this file renders in the ACTIVE SITE's zone. Left alone, those two disagree:
// a coordinator in Chennai scheduling a visit for a Boston site would type
// 09:00, and the calendar — formatting the same instant in Boston time — would
// show 22:30 the day before. The pair below keeps the input on the same clock
// as the display, so what is typed is what is read back.

function partsInZone(d: Date, timeZone: string): Record<string, string> {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
  const out: Record<string, string> = {};
  for (const p of fmt.formatToParts(d)) if (p.type !== 'literal') out[p.type] = p.value;
  // en-CA renders midnight as 24 rather than 00 in some engines.
  if (out.hour === '24') out.hour = '00';
  return out;
}

/**
 * An instant -> the `YYYY-MM-DDTHH:mm` a datetime-local input should show,
 * expressed on the display timezone's clock.
 */
export function toDateTimeInput(
  d: Date | string | number,
  timeZone: string = displayTimeZone,
): string {
  const p = partsInZone(toDate(d), timeZone);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

/**
 * The inverse: a `YYYY-MM-DDTHH:mm` typed on the display timezone's clock ->
 * the UTC instant it names.
 *
 * Read the wall clock as if it were UTC, see what that instant actually reads
 * as in the target zone, and shift by the difference. Two passes, because the
 * offset itself depends on the date (DST). Inside a DST fold the hour is
 * genuinely ambiguous and this resolves it one way — acceptable here, where
 * the value is a clinic appointment, not a measurement.
 */
export function fromDateTimeInput(
  value: string,
  timeZone: string = displayTimeZone,
): string {
  // STRICT on purpose. A datetime-local fires onChange on every keystroke, so
  // half-typed values arrive constantly — and `new Date('2026-')` does not
  // throw, it returns 1 Jan 2026. Accepting whatever Date() can make of the
  // string would let a stray keystroke submit a real but wrong visit date.
  // Only the exact shape the input produces is accepted; anything else is
  // "not ready yet" and comes back empty.
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value);
  if (!m) return '';
  const asUtc = new Date(`${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6] ?? '00'}Z`);
  if (Number.isNaN(asUtc.getTime())) return '';
  let guess = asUtc;
  for (let i = 0; i < 2; i += 1) {
    const p = partsInZone(guess, timeZone);
    const seen = Date.parse(`${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}Z`);
    guess = new Date(guess.getTime() + (asUtc.getTime() - seen));
  }
  return guess.toISOString();
}

/**
 * The calendar days a scheduled visit may be moved to — `YYYY-MM-DD` strings
 * for a date/datetime input's `min` and `max`.
 *
 * Mirrors services/visits/compliance.ts::visitWindowBounds on the server. The
 * server is what decides; this only stops the picker offering a day that will
 * be refused. Returns null where there is no window (unscheduled visits, or
 * no planned date), in which case the input should be left unbounded.
 */
export function visitWindowBounds(v: {
  planned_date?: string | null;
  scheduled_at?: string | null;
  window_minus_days?: number | null;
  window_plus_days?: number | null;
  unscheduled?: boolean | null;
  visit_type?: string | null;
}): { min: string; max: string } | null {
  if (v.unscheduled === true || v.visit_type === 'unscheduled') return null;
  const planned = v.planned_date ?? v.scheduled_at;
  if (!planned) return null;
  // The planned day on the SITE's clock, as the server now reads it
  // (23 Oct 2026). A date-only value keeps its digits.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(siteDayKey(planned));
  if (!m) return null;
  const dayMs = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const ONE_DAY = 86_400_000;
  const iso = (ms: number) => new Date(ms).toISOString().slice(0, 10);
  return {
    min: iso(dayMs - (v.window_minus_days ?? 0) * ONE_DAY),
    max: iso(dayMs + (v.window_plus_days ?? 0) * ONE_DAY),
  };
}

/**
 * WHEN A VISIT IS ACTUALLY HAPPENING — the appointment, not the protocol target.
 *
 * `planned_date` is the protocol-derived date and the anchor the ± window is
 * measured from, so rescheduling deliberately does not move it; it writes
 * `scheduled_at`. Every surface used to read `planned_date ?? scheduled_at`,
 * which is the wrong way round: a rescheduled visit kept showing its old slot
 * on the calendar, the subject timeline and the dashboard, so rescheduling
 * looked like it had silently failed.
 *
 * Both columns are populated on every visit the generator makes (verified
 * across staging and production), so for a visit nobody has moved these are
 * the same instant and this changes nothing.
 *
 * Use `planned_date` directly ONLY where the protocol target is the point —
 * the "Planned date" field, and the window arithmetic.
 */
export function visitWhen(v: {
  planned_date?: string | null;
  scheduled_at?: string | null;
}): string | null {
  return v.scheduled_at ?? v.planned_date ?? null;
}

/**
 * Where a visit sits on the calendar (client, 16 Oct 2026): the day it
 * actually took place once that is recorded, else its appointment. A visit
 * done two days late used to stay on its planned day, because the calendar
 * only ever read the appointment.
 */
export function visitCalendarWhen(v: {
  planned_date?: string | null;
  scheduled_at?: string | null;
  actual_date?: string | null;
}): string | null {
  return v.actual_date || visitWhen(v);
}

/**
 * A value stored as midnight UTC carries a calendar day, not a moment: an
 * actual visit date recorded as "2026-10-09" is saved as 2026-10-09T00:00:00.
 * Formatting it in a site's time zone behind UTC would show the day before.
 */
function isMidnightUtc(iso: string): boolean {
  return /T00:00(:00(\.0+)?)?(Z|\+00:00)?$/.test(iso);
}

/** The day of a visit date, without shifting a date-only value a day early. */
export function fmtVisitDay(iso: string): string {
  return isMidnightUtc(iso) ? fmtPlainDay(iso) : fmtDate(iso);
}

/**
 * The calendar day (`YYYY-MM-DD`) a visit date falls on AT THE SITE.
 *
 * Visit dates are UTC instants (Caspio drops the Z), so the first ten
 * characters are the UTC day — a visit at 6:38 PM in Los Angeles is
 * already the next day in UTC (client, 23 Oct 2026: screening done on
 * 22 Oct showed on 23 Oct in the calendar). Read the day on the site's
 * clock instead. A date-only value (stored as midnight UTC) is a day, not
 * a moment, and keeps its digits.
 */
export function siteDayKey(iso: string, timeZone: string = displayTimeZone): string {
  if (!iso.includes('T') || isMidnightUtc(iso)) return iso.slice(0, 10);
  const d = toDate(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  const p = partsInZone(d, timeZone);
  return `${p.year}-${p.month}-${p.day}`;
}

/** Today's `YYYY-MM-DD` on the site's clock. */
export function siteTodayKey(timeZone: string = displayTimeZone): string {
  const p = partsInZone(new Date(), timeZone);
  return `${p.year}-${p.month}-${p.day}`;
}

/** Add whole days to a `YYYY-MM-DD` key. */
export function addDaysToKey(key: string, days: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(key);
  if (!m) return key;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days)).toISOString().slice(0, 10);
}

/** Whole days from `fromKey` to `toKey` (both `YYYY-MM-DD`). */
export function daysBetweenKeys(fromKey: string, toKey: string): number {
  const ms = (k: string) => Date.parse(`${k.slice(0, 10)}T00:00:00Z`);
  return Math.round((ms(toKey) - ms(fromKey)) / 86_400_000);
}

/**
 * The "Actual date" cell of a visit list (client, 23 Oct 2026): the day the
 * visit really took place, or — when none is recorded — a word saying why,
 * so an empty cell is never left to be guessed at. `recorded` is false for
 * the word, so callers can mute it.
 */
export function visitActualDateLabel(v: {
  actual_date?: string | null;
  status?: string | null;
}): { text: string; recorded: boolean } {
  if (v.actual_date) return { text: fmtVisitDay(v.actual_date), recorded: true };
  switch (v.status) {
    case 'missed': return { text: 'Missed', recorded: false };
    case 'cancelled': return { text: 'Cancelled', recorded: false };
    case 'completed': return { text: 'Not recorded', recorded: false };
    default: return { text: 'Not done yet', recorded: false };
  }
}

/** The time of a visit date, or '' when the value carries no time of day. */
export function fmtVisitTime(iso: string): string {
  return isMidnightUtc(iso) ? '' : fmtTime(iso);
}

/**
 * A `YYYY-MM-DD` bound rendered as a plain calendar date.
 *
 * NOT fmtDate: that resolves the instant in the site's timezone, so a bound
 * built at UTC midnight came out a day early for any site behind UTC — the
 * reschedule dialog showed "Oct 6 – Oct 12" for a window that is really
 * Oct 7 – Oct 13. A window bound is a calendar day, not a moment, so it is
 * formatted from its own digits.
 */
export function fmtPlainDay(day: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(day);
  if (!m) return day;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
    .toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}
